// Domain 05 — RouteZero Data Center, Clark Freeport Zone (Clark / Angeles).
//
//                 INTERNET (ISP, unmanaged) ── WEB 192.0.2.10 (cloud apps) · REMOTE 192.0.2.150 (partner user)
//                     │ G0/0 203.0.113.8/30   · routes RouteZero's public block 198.51.100.32/28 to EDGE
//                   EDGE  Internet edge: NAT/PAT (inside G0/1, outside G0/0), default route, OSPF default
//                     │ 10.50.0.0/30
//                   R1  Core
//          10.50.1.0/30 │           │ 10.50.3.0/30 │ 10.50.4.0/30
//                     R2           R3 ─────────── R4      (user-VLAN gateways, HSRP group 10, VIP 10.50.10.1)
//   Service network ── G0/1          G0/1 .2      G0/1 .3
//   10.50.20.0/24      │                  SW-USR
//   SW-SVC: SRV-DNS .10 · SRV-NTP .30 ·   PC-01, PC-02 (DHCP clients, relayed to R2 10.50.20.1)
//           SRV-MON .40 (Syslog/SNMP) · SRV-APP .80 (portal, published as 198.51.100.34)
//
// R2 is the IOS DHCP server for the user VLAN (pool USERS). OSPF area 0 runs on
// EDGE and R1–R4 (LAN interfaces passive); EDGE originates the default route.
// No real production names, addresses or credentials: documentation ranges and
// a lab-only SNMP community.

import { pc, server, router, sw, link, network } from '../../engine/net/model.js';
import { createInterface } from '../../engine/ios/switching.js';
import { renew } from '../../engine/pc/winshell.js';
import { logEvent } from '../../engine/net/services.js';

const M = { 24: '255.255.255.0', 25: '255.255.255.128', 30: '255.255.255.252' };
const ipm = (s) => { const [ip, p] = s.split('/'); return { ip, mask: M[p] }; };

export const ZONE = 'clark.routezero.lab';
export const MON = { ip: '10.50.20.40', community: 'RZ-LAB-RO' }; // lab-only community string
export const PUBLIC = { block: '198.51.100.32', mask: '255.255.255.240', portal: '198.51.100.34', edge: '203.0.113.10' };

export const ADDR = {
  ISP: { 'GigabitEthernet0/0': '203.0.113.9/30', 'GigabitEthernet0/1': '192.0.2.1/25', 'GigabitEthernet0/2': '192.0.2.129/25' },
  EDGE: { 'GigabitEthernet0/0': '203.0.113.10/30', 'GigabitEthernet0/1': '10.50.0.1/30' },
  R1: { 'GigabitEthernet0/0': '10.50.0.2/30', 'GigabitEthernet0/1': '10.50.1.1/30', 'GigabitEthernet0/2': '10.50.3.1/30', 'GigabitEthernet0/3': '10.50.4.1/30' },
  R2: { 'GigabitEthernet0/0': '10.50.1.2/30', 'GigabitEthernet0/1': '10.50.20.1/24' },
  R3: { 'GigabitEthernet0/0': '10.50.3.2/30', 'GigabitEthernet0/1': '10.50.10.2/24' },
  R4: { 'GigabitEthernet0/0': '10.50.4.2/30', 'GigabitEthernet0/1': '10.50.10.3/24' },
};
export const DESC = {
  ISP: {}, EDGE: { 'GigabitEthernet0/0': 'ISP-INTERNET', 'GigabitEthernet0/1': 'TO-CORE-R1' },
  R1: { 'GigabitEthernet0/0': 'TO-EDGE', 'GigabitEthernet0/1': 'TO-R2-SERVICES', 'GigabitEthernet0/2': 'TO-R3-USERS', 'GigabitEthernet0/3': 'TO-R4-USERS' },
  R2: { 'GigabitEthernet0/0': 'TO-CORE-R1', 'GigabitEthernet0/1': 'SERVICE-NETWORK' },
  R3: { 'GigabitEthernet0/0': 'TO-CORE-R1', 'GigabitEthernet0/1': 'USER-VLAN' },
  R4: { 'GigabitEthernet0/0': 'TO-CORE-R1', 'GigabitEthernet0/1': 'USER-VLAN' },
};
const PORTS = {
  ISP: ['GigabitEthernet0/0', 'GigabitEthernet0/1', 'GigabitEthernet0/2'],
  EDGE: ['GigabitEthernet0/0', 'GigabitEthernet0/1', 'GigabitEthernet0/2'],
  R1: ['GigabitEthernet0/0', 'GigabitEthernet0/1', 'GigabitEthernet0/2', 'GigabitEthernet0/3'],
  R2: ['GigabitEthernet0/0', 'GigabitEthernet0/1', 'GigabitEthernet0/2'],
  R3: ['GigabitEthernet0/0', 'GigabitEthernet0/1', 'GigabitEthernet0/2'],
  R4: ['GigabitEthernet0/0', 'GigabitEthernet0/1', 'GigabitEthernet0/2'],
};
const SEQ = { R1: 1, R2: 2, R3: 3, R4: 4, EDGE: 5, ISP: 9 };
// Each router's free-running clock before NTP corrects it (ms from lab time).
export const SKEW = { EDGE: -41 * 60e3, R1: 3 * 3600e3 + 12 * 60e3, R2: -2 * 86400e3, R3: -(29 * 3600e3), R4: 17 * 60e3 };

export function mkRouter(id, { addressed = true } = {}) {
  const r = router(id, { seq: SEQ[id], ports: PORTS[id] });
  if (addressed) {
    for (const [n, a] of Object.entries(ADDR[id])) Object.assign(createInterface(r, n), { ip: ipm(a), shutdown: false, description: DESC[id]?.[n] || '' });
  }
  if (id === 'ISP') r.managed = false;
  else r.clockSkewMs = SKEW[id];
  return r;
}
export const S = (r, net, mask, nh, ad) => { r.routes.push({ net, mask, ...(/^(Gigabit|Null)/.test(nh) ? { ifc: nh } : { nh }), ...(ad ? { ad } : {}) }); };
const svr = (id, ip, mac, services, extra = {}) => Object.assign(server(id, { ip, mask: '255.255.255.0', gw: '10.50.20.1', dns: ['10.50.20.10'], mac, services, http: extra.http }), extra);

/**
 * Build the Clark data center. Parts:
 *   r4        second user-VLAN gateway (default true). Without it R3 G0/1 is the plain gateway 10.50.10.1.
 *   ospf      OSPF area 0 on EDGE/R1–R4 (default true)
 *   dhcp      R2 DHCP server + relay on the gateways (default true); 'local' = pool on R3 instead
 *   nat       EDGE PAT + static NAT for the portal (default true)
 *   hsrp      HSRP group 10 on R3/R4 (default true, needs r4)
 *   ntp       routers synchronise to SRV-NTP (default true)
 *   mon       Syslog + SNMP to SRV-MON (default true)
 *   portalDns A record for the portal exists (default true)
 */
export function dc({ r4 = true, ospf = true, dhcp = true, nat = true, hsrp = true, ntp = true, mon = true, portalDns = true } = {}) {
  const ISP = mkRouter('ISP');
  const EDGE = mkRouter('EDGE');
  const R1 = mkRouter('R1');
  const R2 = mkRouter('R2');
  const R3 = mkRouter('R3');
  const devs = [ISP, EDGE, R1, R2, R3];
  const links = [];
  S(ISP, PUBLIC.block, PUBLIC.mask, PUBLIC.edge); // the carrier routes RouteZero's public block to EDGE
  devs.push(
    server('WEB', { ip: '192.0.2.10', mask: M[25], gw: '192.0.2.1', mac: '00155d7aff10', services: [{ proto: 'tcp', port: 443, name: 'https' }, { proto: 'tcp', port: 80, name: 'http' }], http: { title: 'Cloud apps', body: 'www.example.com' } }),
    pc('REMOTE', { ip: '192.0.2.150', mask: M[25], gw: '192.0.2.129', mac: '0050797cff50', dns: [] }),
  );
  links.push(link('WEB', 'FastEthernet0', 'ISP', 'GigabitEthernet0/1'), link('REMOTE', 'FastEthernet0', 'ISP', 'GigabitEthernet0/2'),
    link('EDGE', 'GigabitEthernet0/0', 'ISP', 'GigabitEthernet0/0', { type: 'Cat6 UTP (ISP handoff)' }),
    link('EDGE', 'GigabitEthernet0/1', 'R1', 'GigabitEthernet0/0', { type: 'OM4 fiber', connector: 'LC' }),
    link('R1', 'GigabitEthernet0/1', 'R2', 'GigabitEthernet0/0', { type: 'OM4 fiber', connector: 'LC' }),
    link('R1', 'GigabitEthernet0/2', 'R3', 'GigabitEthernet0/0', { type: 'OM4 fiber', connector: 'LC' }));
  S(EDGE, '0.0.0.0', '0.0.0.0', '203.0.113.9');

  // service network
  const SW_SVC = sw('SW-SVC', { seq: 11, fastPorts: 8 });
  devs.push(SW_SVC,
    svr('SRV-DNS', '10.50.20.10', '00155d7c2010', [{ proto: 'udp', port: 53, name: 'dns' }], {
      dnsZone: ZONE, dnsName: `dns.${ZONE}`, authoritative: true,
      dnsRecords: { [`dns.${ZONE}`]: '10.50.20.10', [`ntp.${ZONE}`]: '10.50.20.30', [`nms.${ZONE}`]: '10.50.20.40', ...(portalDns ? { [`portal.${ZONE}`]: '10.50.20.80' } : {}), 'www.example.com': '192.0.2.10' },
    }),
    svr('SRV-NTP', '10.50.20.30', '00155d7c2030', [{ proto: 'udp', port: 123, name: 'ntp' }], { ntpServer: { stratum: 1, refId: '.GPS.' } }),
    svr('SRV-MON', '10.50.20.40', '00155d7c2040', [{ proto: 'udp', port: 514, name: 'syslog' }, { proto: 'udp', port: 162, name: 'snmptrap' }, { proto: 'tcp', port: 443, name: 'https' }]),
    svr('SRV-APP', '10.50.20.80', '00155d7c2080', [{ proto: 'tcp', port: 443, name: 'https' }, { proto: 'tcp', port: 80, name: 'http' }], { http: { title: 'RouteZero Portal', body: `portal.${ZONE}` } }));
  links.push(...['SRV-DNS', 'SRV-NTP', 'SRV-MON', 'SRV-APP'].map((s, i) => link(s, 'FastEthernet0', 'SW-SVC', `FastEthernet0/${i + 1}`)),
    link('SW-SVC', 'GigabitEthernet0/1', 'R2', 'GigabitEthernet0/1'));

  // user VLAN
  const SW_USR = sw('SW-USR', { seq: 12, fastPorts: 8 });
  devs.push(SW_USR, pc('PC-01', { dhcp: true, mac: '0050797c5050' }), pc('PC-02', { dhcp: true, mac: '0050797c5051' }));
  links.push(link('PC-01', 'FastEthernet0', 'SW-USR', 'FastEthernet0/1'), link('PC-02', 'FastEthernet0', 'SW-USR', 'FastEthernet0/2'),
    link('SW-USR', 'GigabitEthernet0/1', 'R3', 'GigabitEthernet0/1'));
  if (r4) {
    const R4 = mkRouter('R4');
    devs.push(R4);
    links.push(link('R1', 'GigabitEthernet0/3', 'R4', 'GigabitEthernet0/0', { type: 'OM4 fiber', connector: 'LC' }), link('SW-USR', 'GigabitEthernet0/2', 'R4', 'GigabitEthernet0/1'));
  } else {
    R3.interfaces['GigabitEthernet0/1'].ip = ipm('10.50.10.1/24');
    Object.assign(R1.interfaces['GigabitEthernet0/3'], { ip: null, shutdown: true, description: '' });
  }
  const n = network(devs, links);
  if (ospf) ospfCore(n);
  if (dhcp === 'local') localDhcp(n); else if (dhcp) dhcpServer(n);
  if (nat) natEdge(n);
  if (hsrp && r4) hsrpDesign(n);
  if (ntp) ntpDesign(n);
  if (mon) monDesign(n);
  bootClients(n);
  return n;
}

// ------------------------------------------------------------ presets
const area0 = (net, wild) => ({ net, wild, area: 0 });
export function ospfCore(n) {
  const P = { routerId: null, networks: [], passive: [], passiveDefault: false, notPassive: [], defaultOriginate: false };
  const cfg = {
    EDGE: { routerId: '5.5.5.5', networks: [area0('10.50.0.0', '0.0.0.3')], defaultOriginate: true },
    R1: { routerId: '1.1.1.1', networks: [area0('10.50.0.0', '0.0.255.255')] },
    R2: { routerId: '2.2.2.2', networks: [area0('10.50.0.0', '0.0.255.255')], passive: ['GigabitEthernet0/1'] },
    R3: { routerId: '3.3.3.3', networks: [area0('10.50.0.0', '0.0.255.255')], passive: ['GigabitEthernet0/1'] },
    R4: { routerId: '4.4.4.4', networks: [area0('10.50.0.0', '0.0.255.255')], passive: ['GigabitEthernet0/1'] },
  };
  for (const [id, c] of Object.entries(cfg)) if (n.devices[id]) n.devices[id].ospf = { 1: { ...P, ...c } };
  return n;
}
export const USERS_POOL = { name: 'USERS', network: '10.50.10.0', mask: '255.255.255.0', router: '10.50.10.1', dns: ['10.50.20.10'], domain: ZONE };
/** Central DHCP server on R2; the user-VLAN gateways relay to it. */
export function dhcpServer(n, { relay = true } = {}) {
  const R2 = n.devices.R2;
  R2.dhcp.pools.USERS = { ...USERS_POOL, dns: [...USERS_POOL.dns] };
  R2.dhcp.excluded.push(['10.50.10.1', '10.50.10.49']);
  if (relay) for (const id of ['R3', 'R4']) if (n.devices[id]) n.devices[id].interfaces['GigabitEthernet0/1'].helper = '10.50.20.1';
  return n;
}
/** Before centralisation: a local pool on the user gateway R3. */
export function localDhcp(n) {
  const R3 = n.devices.R3;
  R3.dhcp.pools.LOCAL = { ...USERS_POOL, name: 'LOCAL', dns: [...USERS_POOL.dns] };
  R3.dhcp.excluded.push(['10.50.10.1', '10.50.10.49']);
  return n;
}
export const NAT_ACL = [{ action: 'permit', base: '10.50.0.0', wild: '0.0.255.255', text: '10.50.0.0 0.0.255.255' }];
export function natEdge(n, { pat = true, statics = true } = {}) {
  const E = n.devices.EDGE;
  E.interfaces['GigabitEthernet0/1'].nat = 'inside';
  E.interfaces['GigabitEthernet0/0'].nat = 'outside';
  E.acls[10] = NAT_ACL.map((x) => ({ ...x }));
  if (pat) E.nat.rules.push({ acl: '10', ifc: 'GigabitEthernet0/0', overload: true });
  if (statics) E.nat.statics = [{ local: '10.50.20.80', global: PUBLIC.portal }];
  return n;
}
export function hsrpDesign(n) {
  const { R3, R4 } = n.devices;
  R3.interfaces['GigabitEthernet0/1'].fhrp = [{ proto: 'hsrp', group: 10, vip: '10.50.10.1', priority: 110, preempt: true }];
  R4.interfaces['GigabitEthernet0/1'].fhrp = [{ proto: 'hsrp', group: 10, vip: '10.50.10.1', priority: 100, preempt: true }];
  return n;
}
export const ROUTERS = ['EDGE', 'R1', 'R2', 'R3', 'R4'];
export function ntpDesign(n, ids = ROUTERS) {
  for (const id of ids) if (n.devices[id]) n.devices[id].ntp = { servers: ['10.50.20.30'], master: null };
  return n;
}
export function monDesign(n, ids = ROUTERS) {
  for (const id of ids) {
    const d = n.devices[id];
    if (!d) continue;
    d.timestamps = true;
    d.logging = { hosts: [MON.ip], trap: 'informational', buffered: true };
    d.snmp = { communities: { [MON.community]: 'ro' }, hosts: [{ ip: MON.ip, version: '2c', community: MON.community }], traps: true, location: `Clark DC rack ${id}`, contact: 'noc@routezero.example' };
  }
  return n;
}
/** Routers boot (log buffer) and DHCP clients request their first lease. */
export function bootClients(n) {
  for (const id of ROUTERS) {
    const d = n.devices[id];
    if (!d) continue;
    d.logs = [];
    if (d.logging) {
      logEvent(n, id, '%SYS-5-RESTART: System restarted --', -26 * 3600e3);
      logEvent(n, id, `%SYS-5-CONFIG_I: Configured from console by admin on vty0 (${MON.ip})`, -95 * 60e3);
    }
  }
  for (const id of ['PC-01', 'PC-02']) if (n.devices[id]?.ipcfg.dhcp) renew(n, n.devices[id]);
  return n;
}
export const reboot = bootClients;

// ------------------------------------------------------------ diagram
const NODE = {
  ISP: { x: 520, y: 58, type: 'cloud', sub: 'Internet · unmanaged' },
  WEB: { x: 790, y: 55, type: 'server', sub: 'Cloud apps 192.0.2.10' },
  REMOTE: { x: 960, y: 85, type: 'pc', sub: 'Partner 192.0.2.150' },
  EDGE: { x: 520, y: 180, type: 'router', sub: 'Internet edge · NAT/PAT' },
  R1: { x: 520, y: 305, type: 'router', sub: 'Core' },
  R2: { x: 250, y: 455, type: 'router', sub: 'Services · DHCP server' },
  'SW-SVC': { x: 250, y: 528, type: 'switch', sub: 'Service switch' },
  'SRV-DNS': { x: 80, y: 600, type: 'server', sub: 'DNS .10' },
  'SRV-NTP': { x: 200, y: 600, type: 'server', sub: 'NTP .30' },
  'SRV-MON': { x: 320, y: 600, type: 'server', sub: 'Syslog/SNMP .40' },
  'SRV-APP': { x: 450, y: 600, type: 'server', sub: 'Portal .80' },
  R3: { x: 720, y: 455, type: 'router', sub: 'User gateway A' },
  R4: { x: 950, y: 455, type: 'router', sub: 'User gateway B' },
  'SW-USR': { x: 835, y: 528, type: 'switch', sub: 'User access' },
  'PC-01': { x: 750, y: 605, type: 'pc', sub: 'DHCP client' },
  'PC-02': { x: 920, y: 605, type: 'pc', sub: 'DHCP client' },
};
const LABELS = {
  'EDGE:GigabitEthernet0/1': '10.50.0.1 inside', 'R1:GigabitEthernet0/0': '.2',
  'R1:GigabitEthernet0/1': '.1', 'R2:GigabitEthernet0/0': '.2', 'R1:GigabitEthernet0/2': '.1', 'R3:GigabitEthernet0/0': '.2', 'R1:GigabitEthernet0/3': '.1', 'R4:GigabitEthernet0/0': '.2',
  'R2:GigabitEthernet0/1': '.1', 'R3:GigabitEthernet0/1': '.2', 'R4:GigabitEthernet0/1': '.3',
};
export function dcLayout(n, { title, notes = {} } = {}) {
  const nodes = Object.fromEntries(Object.entries(NODE).filter(([id]) => n.devices[id]));
  const gw = n.devices.R4 ? 'VIP 10.50.10.1' : 'gateway 10.50.10.1';
  if (!n.devices.R4) { nodes.R3 = { ...nodes.R3, sub: 'User gateway' }; }
  const labels = { ...LABELS, ...(n.devices.R4 ? {} : { 'R3:GigabitEthernet0/1': '.1' }) };
  const ln = {
    'EDGE:GigabitEthernet0/0--ISP:GigabitEthernet0/0': '203.0.113.8/30 · EDGE .10 = NAT outside',
    'R1:GigabitEthernet0/1--R2:GigabitEthernet0/0': '10.50.1.0/30',
    'R1:GigabitEthernet0/2--R3:GigabitEthernet0/0': '10.50.3.0/30',
    'R1:GigabitEthernet0/3--R4:GigabitEthernet0/0': '10.50.4.0/30',
    ...notes,
  };
  return {
    width: 1100, height: 670, title: title || 'RouteZero Data Center — Clark', nodes,
    zones: [
      { x: 380, y: 8, w: 690, h: 125, label: `INTERNET · public ${PUBLIC.block}/28`, cloud: true },
      { x: 25, y: 400, w: 510, h: 255, label: 'SERVICE NETWORK · 10.50.20.0/24' },
      { x: 640, y: 400, w: 435, h: 255, label: `USER VLAN · 10.50.10.0/24 · ${gw}` },
    ],
    linkLabels: labels, linkNotes: ln, overlays: [],
  };
}
